import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Download, RefreshCw, Edit, Trash2, Building, Users, Server, CheckCircle, Eye } from 'lucide-react';
import DataTable from '../../components/common/DataTable';
import type { Column } from '../../components/common/DataTable';
import StatCard from '../../components/common/StatCard';
import TenantModal from '../../components/admin/TenantModal';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import { tenantService } from '../../services';
import type { Tenant as ApiTenant } from '../../services';
import channelService, { type Channel } from '../../services/channel.service';
import { useToast } from '../../components/common/ToastContainer';
import { usePermissions } from '../../hooks/usePermissions';
import { useTenantStore } from '../../stores/tenantStore';

interface Tenant {
  id: string;
  name: string;
  domain: string;
  userCount: number;
  deviceCount: number;
  appUsers: number;
  devices: number;
  admins: number;
  edges: number;
  mtges: number;
  connectors: number;
  channelId: string | null;
  status: 'Active' | 'Suspended';
  createdAt: string;
}

const Tenants = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { canCreate, canUpdate, canDelete } = usePermissions();
  const [loading, setLoading] = useState(false);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [channels, setChannels] = useState<Channel[]>([]);
  const [page] = useState(1);
  const [pageSize] = useState(20);

  // Modal states
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingTenant, setEditingTenant] = useState<Tenant | undefined>();
  const [deleteTenant, setDeleteTenant] = useState<Tenant | undefined>();
  const [isDeleting, setIsDeleting] = useState(false);

  const mapApiTenantToUi = (apiTenant: ApiTenant): Tenant => {
    const a = apiTenant as any;
    return {
      id: a.id,
      name: a.name,
      domain: a.domain,
      userCount: a.appUsers || 0,
      deviceCount: a.devices || 0,
      appUsers: a.appUsers || 0,
      devices: a.devices || 0,
      admins: a.admins || 0,
      edges: a.edges || 0,
      mtges: a.mtges || 0,
      connectors: a.connectors || 0,
      channelId: a.channelId ?? null,
      status: 'Active',
      createdAt: a.createTime || '-',
    };
  };

  // Fetch tenants from API
  const fetchTenants = async () => {
    try {
      setLoading(true);
      const response = await tenantService.list({ page, pageSize });
      const uiTenants = response.items.map(mapApiTenantToUi);
      setTenants(uiTenants);
    } catch (error) {
      console.error('Failed to fetch tenants:', error);
      // Keep existing tenants on error
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTenants();
  }, [page, pageSize]);

  // Channels, for the Channel column and the create-tenant picker. Best effort:
  // a deployment with no channels simply shows every tenant as "Direct".
  useEffect(() => {
    channelService
      .listAll()
      .then(setChannels)
      .catch(() => setChannels([]));
  }, []);

  const channelName = (channelId: string | null) =>
    channelId ? channels.find((c) => c.id === channelId)?.name ?? channelId : null;

  const handleRefresh = () => {
    fetchTenants();
  };

  const handleSaveTenant = async (tenantData: any) => {
    try {
      if (editingTenant) {
        // Update existing tenant
        await tenantService.update(tenantData.id, {
          name: tenantData.name,
          description: tenantData.description,
        });
      } else {
        // Create new tenant
        await tenantService.create({
          name: tenantData.name,
          domain: tenantData.domain,
          organizationName: tenantData.organizationName,
          description: tenantData.description,
          defaultRole: tenantData.defaultRole,
          // Omitted entirely when "Direct" is chosen, so the tenant is created
          // with no channel rather than with an empty-string one.
          channelId: tenantData.channelId || undefined,
        });
      }
      // Refresh the list + header tenant selector
      await fetchTenants();
      const selectData = await tenantService.getSelect();
      useTenantStore.getState().setTenants(selectData.filter((t: any) => t.name !== 'default'));
      setIsAddModalOpen(false);
      setEditingTenant(undefined);
    } catch (error) {
      console.error('Failed to save tenant:', error);
      toast.error('Save Failed', 'Failed to save tenant. Please try again.');
    }
  };

  const handleEditClick = (tenant: Tenant) => {
    setEditingTenant(tenant);
  };

  const handleDeleteClick = (tenant: Tenant) => {
    setDeleteTenant(tenant);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTenant) return;

    try {
      setIsDeleting(true);
      await tenantService.delete(deleteTenant.id);
      await fetchTenants();
      setDeleteTenant(undefined);
    } catch (error) {
      console.error('Failed to delete tenant:', error);
      // Surface the API's actual reason (e.g. "has active resources: 3 user(s)…")
      // instead of a generic message the user can't act on.
      const message =
        (error as any)?.response?.data?.error?.message ||
        (error as any)?.response?.data?.message ||
        (error as any)?.message ||
        'Failed to delete tenant. Please try again.';
      toast.error('Delete Failed', message);
    } finally {
      setIsDeleting(false);
    }
  };

  const columns: Column<Tenant>[] = [
    {
      key: 'name',
      label: 'Tenant Name',
      sortable: true,
      render: (t) => (
        <div className="flex items-center space-x-2">
          <Building className="w-4 h-4 text-primary-600" />
          <button
            onClick={() => navigate(`/admin/tenants/${t.id}`)}
            className="font-medium text-left hover:text-primary-600 transition-colors"
          >
            {t.name}
          </button>
        </div>
      )
    },
    {
      key: 'channelId',
      label: 'Channel',
      sortable: true,
      render: (t) => {
        const name = channelName(t.channelId);
        return name
          ? <span className="text-gray-600">{name}</span>
          // Legacy tenants predating the channel layer belong to no channel.
          : <span className="text-gray-500 italic">Direct</span>;
      },
    },
    { key: 'edges', label: 'Edges', sortable: true, render: (t) => <span className="text-gray-600">{t.edges}</span> },
    { key: 'mtges', label: 'MTGEs', sortable: true, render: (t) => <span className="text-gray-600">{t.mtges}</span> },
    { key: 'connectors', label: 'Connectors', sortable: true, render: (t) => <span className="text-gray-600">{t.connectors}</span> },
    { key: 'devices', label: 'Devices', sortable: true, render: (t) => <div className="flex items-center space-x-1"><Server className="w-3 h-3 text-gray-400" /><span>{t.devices}</span></div> },
    { key: 'admins', label: 'Admins', sortable: true, render: (t) => <span className="text-gray-600">{t.admins}</span> },
    { key: 'appUsers', label: 'VPN Users', sortable: true, render: (t) => <div className="flex items-center space-x-1"><Users className="w-3 h-3 text-gray-400" /><span>{t.appUsers}</span></div> },
    { key: 'status', label: 'Status', sortable: true, render: (t) => <span className={`px-2 py-1 text-xs font-medium rounded-full ${t.status === 'Active' ? 'bg-success-100 text-success-700' : 'bg-gray-100 text-gray-700'}`}>{t.status}</span> },
    {
      key: 'actions',
      label: 'Actions',
      render: (t) => (
        <div className="flex justify-end space-x-2">
          <button
            onClick={() => navigate(`/admin/tenants/${t.id}`)}
            className="p-1.5 hover:bg-gray-100 rounded transition-colors"
            title="View Details"
          >
            <Eye className="w-4 h-4 text-gray-600 hover:text-primary-600" />
          </button>
          {canUpdate && (
          <button
            onClick={() => handleEditClick(t)}
            className="p-1.5 hover:bg-gray-100 rounded transition-colors"
            title="Edit Tenant"
          >
            <Edit className="w-4 h-4 text-gray-600 hover:text-primary-600" />
          </button>
          )}
          {canDelete && (
          <button
            onClick={() => handleDeleteClick(t)}
            className="p-1.5 hover:bg-gray-100 rounded transition-colors"
            title="Delete Tenant"
          >
            <Trash2 className="w-4 h-4 text-gray-600 hover:text-red-600" />
          </button>
          )}
        </div>
      )
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div><h1 className="text-3xl font-bold text-gray-900">Tenants</h1><p className="text-gray-600 mt-1">Manage tenant accounts and resources</p></div>
        <div className="flex space-x-3"><button className="btn-secondary inline-flex items-center space-x-2"><Download className="w-4 h-4" /><span>Export</span></button><button onClick={handleRefresh} className="btn-secondary inline-flex items-center space-x-2"><RefreshCw className="w-4 h-4" /><span>Refresh</span></button>{canCreate && <button onClick={() => setIsAddModalOpen(true)} className="btn-primary inline-flex items-center space-x-2"><Plus className="w-4 h-4" /><span>Add Tenant</span></button>}</div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatCard icon={Building} title="Total Tenants" value={tenants.length} color="primary" />
        <StatCard icon={CheckCircle} title="Active" value={tenants.filter(t => t.status === 'Active').length} color="success" />
        <StatCard icon={Users} title="Total Users" value={tenants.reduce((sum, t) => sum + t.userCount, 0)} color="info" />
        <StatCard icon={Server} title="Total Devices" value={tenants.reduce((sum, t) => sum + t.deviceCount, 0)} color="secondary" />
      </div>
      <div className="card"><DataTable columns={columns} data={tenants} searchPlaceholder="Search tenants..." loading={loading} /></div>

      {/* Add/Edit Tenant Modal */}
      <TenantModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSave={handleSaveTenant}
        mode="add"
      />

      <TenantModal
        isOpen={!!editingTenant}
        onClose={() => setEditingTenant(undefined)}
        onSave={handleSaveTenant}
        tenant={editingTenant}
        mode="edit"
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={!!deleteTenant}
        onClose={() => setDeleteTenant(undefined)}
        onConfirm={handleDeleteConfirm}
        title="Delete Tenant"
        message={`Are you sure you want to delete "${deleteTenant?.name}"?\n\nThis action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        isLoading={isDeleting}
      />
    </div>
  );
};

export default Tenants;
